import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { Button } from '../../../components/ui/Button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { SelectButton } from 'primereact/selectbutton';
import { Skeleton } from 'primereact/skeleton';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';

import Layout from '../../Layout/Layout';
import { useGetAllCompanyDevices } from '../../../queries/devices';
import {
  useGetActiveHappyHour,
  useGetHappyHourTemplates,
  useGetTemplateHistory,
  useStartHappyHour,
  useEndHappyHour,
  useUpdateHappyHourTemplate,
  useDeleteHappyHourTemplate,
  useUploadHappyHourImage,
  useGetImageLibrary,
} from '../../../queries/happyHour';
import { useCustomToast } from '../../Contexts/ToastContext';
import FeatureLocked from '../../SharedComponents/FeatureLocked';
import { useInHub } from '../../../nav/useInHub';

/**
 * Happy Hour — instant promo banners, organised as reusable TEMPLATES with
 * grouped usage history (each template's past runs nest under it).
 *
 * Page structure:
 *   1. Hero with "Create New Promo" CTA.
 *   2. Active-promo card (status === 'ACTIVE') — countdown + End button.
 *   3. "My Promos" — one card per template: [Use Now] [Edit] [Delete] +
 *      collapsible usage history (expanded on the first card).
 *
 * Dialogs:
 *   - Create New Promo: full form → backend find-or-creates the template and
 *     starts a session in one POST /happy-hour/start.
 *   - Use Now: pre-filled from a template, only Duration (+ screens) to set →
 *     POST /happy-hour/start { template_id, ... }.
 *   - Edit: change a template's text/style/image (PUT /templates/{id}); does
 *     not alter past or running sessions.
 */

const DURATION_PRESETS = [15, 30, 45, 60, 120];   // minutes

// Must match the Android HappyHourBannerView style specs.
const STYLE_CARDS = [
  { id: 'urgent',   emoji: '🔴', i18nKey: 'happy_hour.style_urgent',
    gradient: 'linear-gradient(90deg, #7F1D1D, #DC2626, #7F1D1D)',
    accent: '#DC2626', text: '#FFFFFF', timer: '#FFFFFF', progress: '#FFD700', prefix: '🔥' },
  { id: 'friendly', emoji: '🟢', i18nKey: 'happy_hour.style_friendly',
    gradient: 'linear-gradient(90deg, #059669, #047857)',
    accent: '#059669', text: '#FFFFFF', timer: '#FFFFFF', progress: null, prefix: '☕' },
  { id: 'info',     emoji: '🔵', i18nKey: 'happy_hour.style_info',
    gradient: 'linear-gradient(90deg, #2563EB, #1D4ED8)',
    accent: '#2563EB', text: '#FFFFFF', timer: '#FFFFFF', progress: null, prefix: '📢' },
  { id: 'premium',  emoji: '👑', i18nKey: 'happy_hour.style_premium',
    gradient: 'linear-gradient(90deg, #1A1A2E, #DAA520)',
    accent: '#DAA520', text: '#FFD700', timer: '#FFD700', progress: '#FFD700', prefix: '✨' },
];

// Quick-pick promo templates (Arabic primary + English sub-line).
const PROMO_TEMPLATES = [
  { ar: 'خصم 30% على كل المشروبات!',          en: '30% off all drinks!' },
  { ar: 'اشترِ 2 واحصل على الثالث مجاناً',     en: 'Buy 2, get the 3rd free!' },
  { ar: 'عرض خاص: كابتشينو + كيك = 25 ريال',  en: 'Cappuccino + cake = 25 SAR' },
  { ar: 'آخر ساعة! كل الحلويات بنص السعر',    en: 'Last hour! Desserts half price' },
  { ar: 'جرب مشروبنا الجديد!',                 en: 'Try our new drink!' },
];

function styleOf(id) {
  return STYLE_CARDS.find((s) => s.id === id) || STYLE_CARDS[0];
}

function StyleBadge({ style }) {
  const { t } = useTranslation();
  const card = styleOf(style);
  return (
    <span className="pp-status" style={{
      background: card.gradient, color: card.text, border: 'none',
      fontWeight: 600, textShadow: '0 1px 2px rgba(0,0,0,0.35)',
    }}>
      {card.emoji} {t(card.i18nKey)}
    </span>
  );
}

/** At-a-glance tag so the templates list shows Bar vs Full Screen without opening Edit. */
function DisplayModeTag({ displayMode }) {
  const { t } = useTranslation();
  const isFullScreen = displayMode === 'full_screen';
  return (
    <span className="pp-status" style={{
      background: isFullScreen ? 'var(--pp-primary-subtle, #ede9fe)' : 'var(--pp-bg-secondary, #f3f4f6)',
      color: isFullScreen ? 'var(--pp-primary, #7c3aed)' : 'var(--pp-text-secondary, #4b5563)',
      border: '1px solid ' + (isFullScreen ? 'var(--pp-primary, #7c3aed)' : 'var(--pp-border, #d1d5db)'),
      fontWeight: 600,
    }}>
      {isFullScreen ? '🖥️ ' + t('happy_hour.display_mode_full_screen') : '📺 ' + t('happy_hour.display_mode_bar')}
    </span>
  );
}

function formatCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${String(h).padStart(2, '0')}:${mm}:${ss}` : `${mm}:${ss}`;
}

/** Parses a backend timestamp. The API now emits UTC ISO-8601 WITH an offset,
 *  which `new Date()` handles directly; the space->T swap is the fallback for
 *  the retired naive 'Y-m-d H:i' format (which was always UTC, hence the Z). */
function parseApiDate(value) {
  if (!value) return null;
  const raw = String(value);
  const d = raw.includes('T') ? new Date(raw) : new Date(`${raw.replace(' ', 'T')}Z`);
  return isNaN(d.getTime()) ? null : d;
}

function fmtDateTime(value) {
  const d = parseApiDate(value);
  return d ? d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
}

function fmtDate(value) {
  const d = parseApiDate(value);
  return d ? d.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—';
}

export default function HappyHour() {
  const { t } = useTranslation();
  const inHub = useInHub();
  const { showToast } = useCustomToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [useNowTpl, setUseNowTpl]   = useState(null);   // template for Use-Now dialog
  const [editTpl,   setEditTpl]     = useState(null);   // template for Edit dialog

  const { data: activeData, error: activeError } = useGetActiveHappyHour();
  const { data: templatesData, isLoading: templatesLoading, error: templatesError } = useGetHappyHourTemplates();
  const deleteMut = useDeleteHappyHourTemplate();

  const featureError =
    activeError?.response?.status === 402 ? activeError
      : templatesError?.response?.status === 402 ? templatesError
      : null;

  const activeSession = activeData?.session ?? null;
  const templates = useMemo(() => {
    const arr = templatesData?.templates ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [templatesData]);

  const askDelete = (tpl) => {
    confirmDialog({
      message: t('happy_hour.delete_confirm', { promo: tpl.promo_text }),
      header: t('happy_hour.delete_title'),
      icon: 'pi pi-exclamation-triangle',
      acceptClassName: 'p-button-danger',
      acceptLabel: t('happy_hour.delete'),
      rejectLabel: t('buttons.cancel'),
      accept: () => deleteMut.mutate(tpl.id),
    });
  };

  if (featureError) {
    return (
      <Layout>
        <FeatureLocked feature="happy_hour" message={featureError.response.data?.message} />
      </Layout>
    );
  }

  return (
    <Layout>
      <ConfirmDialog />
      <div className="container-fluid py-3" style={{ maxWidth: 980 }}>
        {/* Hero */}
        <div className="pp-card p-5 text-center" style={{
          background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 50%, #fff 100%)',
          borderColor: 'rgba(234, 88, 12, 0.25)',
        }}>
          <div style={{ fontSize: '3.5rem', lineHeight: 1 }}>⏰</div>
          {!inHub && <h2 className="pp-page-title justify-content-center mt-3 mb-1">{t('happy_hour.hero_title')}</h2>}
          <p className="text-muted mb-3" style={{ maxWidth: 560, margin: '0 auto' }}>
            {t('happy_hour.hero_subtitle')}
          </p>
          <Button
            icon="pi pi-plus"
            label={'🔥 ' + t('happy_hour.create_new')}
            onClick={() => setCreateOpen(true)}
            disabled={!!activeSession}
            style={{ fontSize: '1rem', padding: '0.75rem 2rem', minHeight: 52 }}
          />
          {activeSession && (
            <p className="small text-muted mt-2 mb-0">{t('happy_hour.active_blocker')}</p>
          )}
        </div>

        {activeSession && (
          <ActiveHappyHourCard
            session={activeSession}
            onEnded={() => showToast('success', t('happy_hour.title'), t('happy_hour.ended_toast'))}
          />
        )}

        {/* My Promos — templates with grouped history */}
        <div className="d-flex align-items-center justify-content-between mt-4 mb-2">
          <h5 className="mb-0">
            <i className="pi pi-bookmark me-2" style={{ color: 'var(--pp-btn-honey)' }} />
            {t('happy_hour.my_promos')}
          </h5>
          <span className="pp-status pp-status-neutral">{templates.length}</span>
        </div>

        {templatesLoading ? (
          <div className="d-flex flex-column gap-3">
            <Skeleton height="8rem" /><Skeleton height="8rem" />
          </div>
        ) : templates.length === 0 ? (
          <div className="pp-card p-5 text-center text-muted">
            <div style={{ fontSize: '2.5rem' }}>🪧</div>
            <p className="mt-2 mb-0">{t('happy_hour.no_promos')}</p>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {templates.map((tpl, idx) => (
              <TemplateCard
                key={tpl.id}
                template={tpl}
                defaultExpanded={idx === 0}
                disableUse={!!activeSession}
                onUseNow={() => setUseNowTpl(tpl)}
                onEdit={() => setEditTpl(tpl)}
                onDelete={() => askDelete(tpl)}
              />
            ))}
          </div>
        )}
      </div>

      <CreatePromoDialog open={createOpen} onClose={() => setCreateOpen(false)} />
      <UseNowDialog template={useNowTpl} onClose={() => setUseNowTpl(null)} />
      <EditTemplateDialog template={editTpl} onClose={() => setEditTpl(null)} />
    </Layout>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Template card — header (promo + style + usage), action row, and a
 * collapsible run-history list (lazily fetched when expanded).
 * ─────────────────────────────────────────────────────────────────── */
function TemplateCard({ template, defaultExpanded, disableUse, onUseNow, onEdit, onDelete }) {
  const { t } = useTranslation();
  const card = styleOf(template.style);
  const [expanded, setExpanded] = useState(!!defaultExpanded);

  const { data: historyData, isLoading: historyLoading } =
    useGetTemplateHistory(template.id, expanded);
  const sessions = useMemo(() => {
    const arr = historyData?.sessions ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [historyData]);

  return (
    <div className="pp-card p-3" style={{ borderInlineStart: `4px solid ${card.accent}` }}>
      {/* Header */}
      <div className="d-flex align-items-start gap-3">
        {template.promo_image_url ? (
          <img src={template.promo_image_url} alt="" width={52} height={52}
               style={{ objectFit: 'cover', borderRadius: 10, flexShrink: 0 }} />
        ) : (
          <span className="d-inline-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52, borderRadius: 10, background: card.gradient, fontSize: '1.5rem', flexShrink: 0 }}>
            {card.prefix}
          </span>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong dir="auto" className="d-block" style={{ fontSize: '1.05rem' }}>
            {card.prefix} {template.promo_text}
          </strong>
          {template.promo_text_en && (
            <span dir="auto" className="small text-muted d-block">{template.promo_text_en}</span>
          )}
          <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
            <StyleBadge style={template.style} />
            <DisplayModeTag displayMode={template.display_mode} />
            <span className="small text-muted">
              · {t('happy_hour.used_n_times', { n: template.use_count || 0 })}
              {template.last_used_at && <> · {t('happy_hour.last_used', { date: fmtDate(template.last_used_at) })}</>}
            </span>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="d-flex align-items-center gap-2 flex-wrap mt-3">
        <Button
          icon="pi pi-play"
          label={'▶️ ' + t('happy_hour.use_now')}
          disabled={disableUse}
          onClick={onUseNow}
          size="small"
        />
        <Button icon="pi pi-pencil" label={t('happy_hour.edit')} outlined size="small" onClick={onEdit} />
        <Button icon="pi pi-trash" label={t('happy_hour.delete')} outlined severity="danger" size="small" onClick={onDelete} />
      </div>

      {/* Usage history */}
      <div className="mt-3 border-top pt-2">
        <button
          type="button"
          className="btn btn-link p-0 text-decoration-none d-flex align-items-center gap-2"
          style={{ color: 'var(--pp-text-secondary, #6b7280)' }}
          onClick={() => setExpanded((v) => !v)}
        >
          <i className={`pi ${expanded ? 'pi-chevron-down' : 'pi-chevron-right'}`} />
          <span className="small fw-semibold">
            📋 {t('happy_hour.usage_history')} ({template.use_count || 0})
          </span>
        </button>

        {expanded && (
          <div className="mt-2">
            {historyLoading ? (
              <Skeleton height="3rem" />
            ) : sessions.length === 0 ? (
              <p className="small text-muted mb-0 ps-3">{t('happy_hour.no_runs')}</p>
            ) : (
              <ul className="list-unstyled mb-0" style={{ maxHeight: '14rem', overflowY: 'auto' }}>
                {sessions.map((s) => {
                  const sc = styleOf(s.style);
                  return (
                    <li key={s.id} className="d-flex align-items-center justify-content-between py-1 px-2 small"
                        style={{ borderBottom: '1px solid var(--pp-border)' }}>
                      <span>{fmtDateTime(s.starts_at || s.created_at)}</span>
                      <span className="text-muted">
                        {s.duration_minutes} {t('match_scheduler.min')} · {sc.emoji} {t(sc.i18nKey)}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Active promo card — countdown + End button.
 * ─────────────────────────────────────────────────────────────────── */
function ActiveHappyHourCard({ session, onEnded }) {
  const { t } = useTranslation();
  const endMut = useEndHappyHour();
  const card = styleOf(session.style);

  const endsAt = parseApiDate(session.ends_at);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const remainingMs = endsAt ? Math.max(0, endsAt.getTime() - now) : 0;

  const screens = session.device_ids?.length
    ? t('happy_hour.n_screens', { n: session.device_ids.length })
    : t('quick_show.all_screens');

  return (
    <div className="pp-card mt-4 p-4" style={{ borderColor: card.accent, borderWidth: 2 }}>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h5 className="mb-0">🔥 {t('happy_hour.active_title')}</h5>
        <div className="d-flex align-items-center gap-2">
          <StyleBadge style={session.style} />
          <DisplayModeTag displayMode={session.display_mode} />
        </div>
      </div>

      <BannerPreview
        styleId={session.style}
        promoText={session.promo_text}
        promoTextEn={session.promo_text_en}
        imageUrl={session.promo_image_url}
        countdownLabel={formatCountdown(remainingMs)}
        progress={endsAt && session.duration_minutes
          ? remainingMs / (session.duration_minutes * 60_000) : null}
      />

      <div className="d-flex align-items-center justify-content-between flex-wrap gap-3 mt-3">
        <div className="d-flex flex-column">
          <span className="small text-muted">{t('happy_hour.time_remaining')}</span>
          <span style={{ fontSize: '1.8rem', fontWeight: 700, color: card.accent, fontVariantNumeric: 'tabular-nums' }}>
            ⏰ {formatCountdown(remainingMs)}
          </span>
          <span className="small text-muted"><i className="pi pi-desktop me-1" />{screens}</span>
        </div>
        <Button
          icon="pi pi-stop-circle"
          label={'🛑 ' + t('happy_hour.end')}
          severity="danger"
          loading={endMut.isLoading}
          onClick={() => endMut.mutate(session.id, { onSuccess: onEnded })}
        />
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Banner preview — faithful mini-mock of the Android bottom banner.
 * ─────────────────────────────────────────────────────────────────── */
function BannerPreview({ styleId, promoText, promoTextEn, imageUrl, countdownLabel, progress }) {
  const card = styleOf(styleId);
  const circular = styleId === 'urgent';
  return (
    <div style={{
      background: card.gradient, borderRadius: 12, padding: '14px 18px',
      display: 'flex', alignItems: 'center', gap: 14, minHeight: 84,
      boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
    }}>
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          width={56}
          height={56}
          style={{
            objectFit: 'cover',
            borderRadius: circular ? '50%' : 10,
            border: `3px solid ${card.progress || '#fff'}`,
            flexShrink: 0,
          }}
        />
      )}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div dir="auto" style={{
          color: card.text, fontWeight: 700, fontSize: '1.05rem',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {card.prefix} {promoText || '…'}
        </div>
        {promoTextEn && (
          <div dir="auto" style={{ color: card.text, opacity: 0.9, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {promoTextEn}
          </div>
        )}
        {card.progress && progress != null && (
          <div style={{ background: 'rgba(255,255,255,0.3)', height: 6, borderRadius: 3, marginTop: 8, overflow: 'hidden' }}>
            <div style={{
              background: card.progress, height: '100%', borderRadius: 3,
              width: `${Math.max(0, Math.min(100, progress * 100))}%`,
              transition: 'width 1s linear',
            }} />
          </div>
        )}
      </div>
      <div style={{
        color: card.timer, fontWeight: 700, fontFamily: 'JetBrains Mono, monospace',
        fontSize: '1.15rem', whiteSpace: 'nowrap', flexShrink: 0, fontVariantNumeric: 'tabular-nums',
      }}>
        ⏰ {countdownLabel}
      </div>
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Product image picker — grid of the company's IMAGE media library.
 * ─────────────────────────────────────────────────────────────────── */
function MediaImagePickerDialog({ visible, onHide, onPick }) {
  const { t } = useTranslation();
  const { data, isLoading } = useGetImageLibrary(visible);
  const images = useMemo(() => {
    const arr = data?.data ?? data ?? [];
    return Array.isArray(arr) ? arr : [];
  }, [data]);

  return (
    <Dialog
      header={<span>🖼️ {t('happy_hour.pick_from_media')}</span>}
      visible={visible}
      onHide={onHide}
      style={{ width: 'min(95vw, 640px)' }}
      modal
    >
      {isLoading ? (
        <div className="row g-2">{[...Array(6)].map((_, i) => (
          <div key={i} className="col-4"><Skeleton height="7rem" /></div>
        ))}</div>
      ) : images.length === 0 ? (
        <div className="p-4 text-center text-muted small">{t('happy_hour.no_images')}</div>
      ) : (
        <div className="row g-2">
          {images.map((m) => (
            <div key={m.id} className="col-6 col-md-4 col-lg-3">
              <button
                type="button"
                className="p-0 border-0 w-100"
                style={{ background: 'transparent', cursor: 'pointer' }}
                onClick={() => { onPick(m.path); onHide(); }}
                title={m.file_name}
              >
                <img src={m.path} alt={m.file_name}
                     className="w-100"
                     style={{ height: 110, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--pp-border)' }} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Reusable image-field (preview + From Media + Upload/Camera).
 * ─────────────────────────────────────────────────────────────────── */
function ImageField({ imageUrl, setImageUrl }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const uploadMut = useUploadHappyHourImage();
  const [pickerOpen, setPickerOpen] = useState(false);

  const onFilePicked = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    uploadMut.mutate(file, { onSuccess: (url) => setImageUrl(url) });
  };

  return (
    <>
      <div className="d-flex align-items-center gap-2 flex-wrap">
        {imageUrl ? (
          <div className="position-relative" style={{ width: 84, height: 84 }}>
            <img src={imageUrl} alt="" width={84} height={84}
                 style={{ objectFit: 'cover', borderRadius: 12, border: '2px solid var(--pp-primary)' }} />
            <Button
              icon="pi pi-times" rounded text severity="danger"
              className="position-absolute"
              style={{ top: -10, insetInlineEnd: -10 }}
              onClick={() => setImageUrl(null)}
            />
          </div>
        ) : (
          <span className="d-inline-flex align-items-center justify-content-center text-muted"
                style={{ width: 84, height: 84, borderRadius: 12, border: '2px dashed var(--pp-border)', fontSize: '1.6rem' }}>
            📸
          </span>
        )}
        <div className="d-flex flex-column gap-2">
          <Button
            icon="pi pi-folder-open" size="small" outlined
            label={t('happy_hour.from_media')}
            onClick={() => setPickerOpen(true)}
          />
          <Button
            icon="pi pi-camera" size="small" outlined
            label={t('happy_hour.upload_photo')}
            loading={uploadMut.isLoading}
            onClick={() => fileInputRef.current?.click()}
          />
          <input
            ref={fileInputRef} type="file" accept="image/*" capture="environment"
            style={{ display: 'none' }} onChange={onFilePicked}
          />
        </div>
      </div>
      <MediaImagePickerDialog
        visible={pickerOpen}
        onHide={() => setPickerOpen(false)}
        onPick={(url) => setImageUrl(url)}
      />
    </>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Reusable style picker (4 cards).
 * ─────────────────────────────────────────────────────────────────── */
function StylePicker({ style, setStyle }) {
  const { t } = useTranslation();
  return (
    <div className="row g-2">
      {STYLE_CARDS.map((card) => (
        <div key={card.id} className="col-6 col-md-3">
          <button
            type="button"
            className="w-100 p-0 border-0"
            style={{ background: 'transparent', cursor: 'pointer' }}
            onClick={() => setStyle(card.id)}
          >
            <div style={{
              borderRadius: 12, overflow: 'hidden',
              border: style === card.id ? `3px solid ${card.accent}` : '3px solid transparent',
              boxShadow: style === card.id ? `0 0 0 2px ${card.accent}40` : 'none',
            }}>
              <div style={{ background: card.gradient, height: 46, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>
                {card.emoji}
              </div>
              <div className="py-1 small fw-semibold text-center" style={{ background: 'var(--pp-bg-primary, #fff)', border: '1px solid var(--pp-border)', borderTop: 'none', borderRadius: '0 0 9px 9px' }}>
                {t(card.i18nKey)}
              </div>
            </div>
          </button>
        </div>
      ))}
    </div>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Display Mode picker — the existing bottom bar, or the new full-screen
 * overlay. Either way it's an overlay on top of whatever is playing; this
 * only changes which View the player shows.
 * ─────────────────────────────────────────────────────────────────── */
function DisplayModePicker({ displayMode, setDisplayMode }) {
  const { t } = useTranslation();
  const options = [
    { label: `📺 ${t('happy_hour.display_mode_bar')}`, value: 'bar' },
    { label: `🖥️ ${t('happy_hour.display_mode_full_screen')}`, value: 'full_screen' },
  ];
  return (
    <SelectButton
      value={displayMode}
      options={options}
      onChange={(e) => e.value && setDisplayMode(e.value)}
    />
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Reusable screen picker (all vs. specific devices).
 * ─────────────────────────────────────────────────────────────────── */
function ScreenPicker({ open, allScreens, setAllScreens, deviceIds, setDeviceIds }) {
  const { t } = useTranslation();
  const { data: devicesData, isLoading: loadingDevices } = useGetAllCompanyDevices(open);
  const devices = useMemo(() => {
    const arr = Array.isArray(devicesData?.data) ? devicesData.data
              : Array.isArray(devicesData) ? devicesData : [];
    return arr;
  }, [devicesData]);

  const toggleOne = (id, checked) => setDeviceIds((prev) =>
    checked ? [...new Set([...prev, id])] : prev.filter((x) => x !== id)
  );

  return (
    <>
      <label className="d-flex align-items-center gap-2 p-2 rounded fw-semibold"
             style={{ cursor: 'pointer', color: 'var(--pp-btn-honey)' }}>
        <Checkbox checked={allScreens} onChange={(e) => setAllScreens(e.checked)} />
        {t('quick_show.all_screens')}
      </label>
      {!allScreens && (
        loadingDevices ? (
          <Skeleton height="6rem" />
        ) : devices.length === 0 ? (
          <p className="pp-field-error"><i className="pi pi-exclamation-circle" />{t('quick_show.no_screens')}</p>
        ) : (
          <div className="d-flex flex-column gap-2" style={{ maxHeight: '22vh', overflowY: 'auto' }}>
            {devices.map((d) => (
              <label key={d.id} className="d-flex align-items-center gap-2 p-2 rounded"
                     style={{
                       background: deviceIds.includes(d.id) ? 'color-mix(in srgb, var(--pp-btn-honey) 10%, transparent)' : 'transparent',
                       border: deviceIds.includes(d.id) ? '1px solid var(--pp-btn-honey)' : '1px solid var(--pp-border)',
                       cursor: 'pointer',
                     }}>
                <Checkbox checked={deviceIds.includes(d.id)} onChange={(e) => toggleOne(d.id, e.checked)} />
                <span className="fw-semibold">{d.name || d.serial_name}</span>
              </label>
            ))}
          </div>
        )
      )}
    </>
  );
}

function previewCountdown(duration) {
  return duration >= 60 ? formatCountdown(duration * 60_000) : `${String(duration).padStart(2, '0')}:00`;
}

/* ──────────────────────────────────────────────────────────────────────
 * Create New Promo dialog — full form; backend find-or-creates the template
 * and starts the session in one POST.
 * ─────────────────────────────────────────────────────────────────── */
function CreatePromoDialog({ open, onClose }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();

  const [promoText,   setPromoText]   = useState('');
  const [promoTextEn, setPromoTextEn] = useState('');
  const [style,       setStyle]       = useState('urgent');
  const [displayMode, setDisplayMode] = useState('bar');
  const [imageUrl,    setImageUrl]    = useState(null);
  const [duration,    setDuration]    = useState(30);
  const [allScreens,  setAllScreens]  = useState(true);
  const [deviceIds,   setDeviceIds]   = useState([]);

  const startMut = useStartHappyHour();

  useEffect(() => {
    if (open) {
      setPromoText('');
      setPromoTextEn('');
      setStyle('urgent');
      setDisplayMode('bar');
      setImageUrl(null);
      setDuration(30);
      setAllScreens(true);
      setDeviceIds([]);
    }
  }, [open]);

  const canSubmit =
    promoText.trim().length > 0 &&
    (allScreens || deviceIds.length > 0);

  const submit = () => {
    if (!canSubmit) {
      showToast('error', t('happy_hour.title'), t('happy_hour.fill_required'));
      return;
    }
    const payload = {
      promo_text:       promoText.trim(),
      promo_text_en:    promoTextEn.trim() || null,
      style,
      display_mode:     displayMode,
      // Always explicit (never omitted): Create New can resolve to an
      // ALREADY-EXISTING template (same promo text reused) — omitting this
      // key here would leave that template's OLD photo/English line in
      // place instead of clearing it when this form has none.
      promo_image_url:  imageUrl || null,
      duration_minutes: duration,
      ...(!allScreens ? { device_ids: deviceIds } : {}),
    };
    startMut.mutate(payload, { onSuccess: () => onClose?.() });
  };

  return (
    <Dialog
      header={<span>🔥 {t('happy_hour.create_dialog_title')}</span>}
      visible={open}
      onHide={onClose}
      style={{ width: 'min(95vw, 600px)' }}
      modal
    >
      <div className="d-flex flex-column" style={{ gap: '1.1rem' }}>
        {/* Promo message (Arabic) */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-megaphone" />
            {t('happy_hour.promo_label')}
            <span className="pp-field-required">*</span>
          </label>
          <InputTextarea
            value={promoText}
            onChange={(e) => setPromoText(e.target.value)}
            placeholder={t('happy_hour.promo_ph')}
            rows={2}
            autoResize
            dir="auto"
            autoFocus
          />
          <div className="d-flex gap-2 flex-wrap mt-2">
            {PROMO_TEMPLATES.map((tpl) => (
              <button
                key={tpl.ar} type="button"
                className={`pp-chip ${promoText === tpl.ar ? 'pp-chip-active' : ''}`}
                style={{ minHeight: 32 }}
                dir="rtl"
                onClick={() => { setPromoText(tpl.ar); setPromoTextEn(tpl.en); }}
              >
                💡 {tpl.ar}
              </button>
            ))}
          </div>
        </div>

        {/* English (optional) */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-language" />
            {t('happy_hour.promo_en_label')}
          </label>
          <InputText
            value={promoTextEn}
            onChange={(e) => setPromoTextEn(e.target.value)}
            placeholder={t('happy_hour.promo_en_ph')}
          />
        </div>

        {/* Product image (optional) */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-image" />
            {t('happy_hour.image_label')}
          </label>
          <ImageField imageUrl={imageUrl} setImageUrl={setImageUrl} />
        </div>

        {/* Banner style */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-palette" />
            {t('happy_hour.style_label')}
          </label>
          <StylePicker style={style} setStyle={setStyle} />
        </div>

        {/* Display Mode */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-desktop" />
            {t('happy_hour.display_mode_title')}
          </label>
          <DisplayModePicker displayMode={displayMode} setDisplayMode={setDisplayMode} />
        </div>

        {/* Duration */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-clock" />
            {t('happy_hour.duration')}
          </label>
          <div className="d-flex gap-2 flex-wrap">
            {DURATION_PRESETS.map((m) => (
              <button
                key={m} type="button"
                className={`pp-chip ${duration === m ? 'pp-chip-active' : ''}`}
                onClick={() => setDuration(m)}
                style={{ minHeight: 36 }}
              >
                {t('quick_show.minutes', { n: m })}
              </button>
            ))}
          </div>
        </div>

        {/* Screens */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-desktop" />
            {t('quick_show.which_screens')}
            <span className="pp-field-required">*</span>
          </label>
          <ScreenPicker
            open={open}
            allScreens={allScreens} setAllScreens={setAllScreens}
            deviceIds={deviceIds} setDeviceIds={setDeviceIds}
          />
        </div>

        {/* Live preview */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-eye" />
            {t('happy_hour.preview')}
          </label>
          <BannerPreview
            styleId={style}
            promoText={promoText || t('happy_hour.promo_ph')}
            promoTextEn={promoTextEn}
            imageUrl={imageUrl}
            countdownLabel={previewCountdown(duration)}
            progress={1}
          />
        </div>

        <div className="pp-dialog-footer">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={onClose} />
          <Button
            icon="pi pi-bolt"
            label={'🔥 ' + t('happy_hour.start_cta')}
            loading={startMut.isLoading}
            disabled={!canSubmit}
            onClick={submit}
          />
        </div>
      </div>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Use Now dialog — pre-filled from a template; only Duration (+ screens).
 * ─────────────────────────────────────────────────────────────────── */
function UseNowDialog({ template, onClose }) {
  const { t } = useTranslation();
  const open = !!template;

  const [duration,   setDuration]   = useState(30);
  const [allScreens, setAllScreens] = useState(true);
  const [deviceIds,  setDeviceIds]  = useState([]);
  const startMut = useStartHappyHour();

  useEffect(() => {
    if (template) {
      setDuration(30);
      setAllScreens(true);
      setDeviceIds([]);
    }
  }, [template]);

  if (!template) return null;

  const canSubmit = allScreens || deviceIds.length > 0;

  const submit = () => {
    if (!canSubmit) return;
    startMut.mutate({
      template_id:      template.id,
      promo_text:       template.promo_text,
      style:            template.style || 'urgent',
      display_mode:     template.display_mode || 'bar',
      duration_minutes: duration,
      ...(template.promo_text_en   ? { promo_text_en: template.promo_text_en } : {}),
      ...(template.promo_image_url ? { promo_image_url: template.promo_image_url } : {}),
      ...(!allScreens              ? { device_ids: deviceIds } : {}),
    }, { onSuccess: () => onClose?.() });
  };

  return (
    <Dialog
      header={<span dir="auto">▶️ {t('happy_hour.use_now')}: {template.promo_text}</span>}
      visible={open}
      onHide={onClose}
      style={{ width: 'min(95vw, 540px)' }}
      modal
    >
      <div className="d-flex flex-column" style={{ gap: '1.1rem' }}>
        <p className="small text-muted mb-0">{t('happy_hour.prefilled_hint')}</p>

        {/* Pre-filled banner preview (not editable here) */}
        <BannerPreview
          styleId={template.style}
          promoText={template.promo_text}
          promoTextEn={template.promo_text_en}
          imageUrl={template.promo_image_url}
          countdownLabel={previewCountdown(duration)}
          progress={1}
        />

        {/* Duration */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-clock" />
            {t('happy_hour.duration')}
            <span className="pp-field-required">*</span>
          </label>
          <div className="d-flex gap-2 flex-wrap">
            {DURATION_PRESETS.map((m) => (
              <button
                key={m} type="button"
                className={`pp-chip ${duration === m ? 'pp-chip-active' : ''}`}
                onClick={() => setDuration(m)}
                style={{ minHeight: 36 }}
              >
                {t('quick_show.minutes', { n: m })}
              </button>
            ))}
          </div>
        </div>

        {/* Screens */}
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-desktop" />
            {t('quick_show.which_screens')}
            <span className="pp-field-required">*</span>
          </label>
          <ScreenPicker
            open={open}
            allScreens={allScreens} setAllScreens={setAllScreens}
            deviceIds={deviceIds} setDeviceIds={setDeviceIds}
          />
        </div>

        <div className="pp-dialog-footer">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={onClose} />
          <Button
            icon="pi pi-bolt"
            label={'🔥 ' + t('happy_hour.start_now')}
            loading={startMut.isLoading}
            disabled={!canSubmit}
            onClick={submit}
          />
        </div>
      </div>
    </Dialog>
  );
}

/* ──────────────────────────────────────────────────────────────────────
 * Edit template dialog — change a promo's text/style/image. Does not touch
 * running or historical sessions.
 * ─────────────────────────────────────────────────────────────────── */
function EditTemplateDialog({ template, onClose }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const open = !!template;

  const [promoText,   setPromoText]   = useState('');
  const [promoTextEn, setPromoTextEn] = useState('');
  const [style,       setStyle]       = useState('urgent');
  const [displayMode, setDisplayMode] = useState('bar');
  const [imageUrl,    setImageUrl]    = useState(null);

  const updateMut = useUpdateHappyHourTemplate();

  useEffect(() => {
    if (template) {
      setPromoText(template.promo_text || '');
      setPromoTextEn(template.promo_text_en || '');
      setStyle(template.style || 'urgent');
      setDisplayMode(template.display_mode || 'bar');
      setImageUrl(template.promo_image_url || null);
    }
  }, [template]);

  if (!template) return null;

  const canSubmit = promoText.trim().length > 0;

  const submit = () => {
    if (!canSubmit) {
      showToast('error', t('happy_hour.title'), t('happy_hour.fill_required'));
      return;
    }
    updateMut.mutate({
      id:              template.id,
      promo_text:      promoText.trim(),
      promo_text_en:   promoTextEn.trim() || null,
      style,
      display_mode:    displayMode,
      promo_image_url: imageUrl || null,
    }, { onSuccess: () => onClose?.() });
  };

  return (
    <Dialog
      header={<span>✏️ {t('happy_hour.edit_dialog_title')}</span>}
      visible={open}
      onHide={onClose}
      style={{ width: 'min(95vw, 600px)' }}
      modal
    >
      <div className="d-flex flex-column" style={{ gap: '1.1rem' }}>
        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-megaphone" />
            {t('happy_hour.promo_label')}
            <span className="pp-field-required">*</span>
          </label>
          <InputTextarea
            value={promoText}
            onChange={(e) => setPromoText(e.target.value)}
            placeholder={t('happy_hour.promo_ph')}
            rows={2}
            autoResize
            dir="auto"
            autoFocus
          />
        </div>

        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-language" />
            {t('happy_hour.promo_en_label')}
          </label>
          <InputText
            value={promoTextEn}
            onChange={(e) => setPromoTextEn(e.target.value)}
            placeholder={t('happy_hour.promo_en_ph')}
          />
        </div>

        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-image" />
            {t('happy_hour.image_label')}
          </label>
          <ImageField imageUrl={imageUrl} setImageUrl={setImageUrl} />
        </div>

        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-palette" />
            {t('happy_hour.style_label')}
          </label>
          <StylePicker style={style} setStyle={setStyle} />
        </div>

        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-desktop" />
            {t('happy_hour.display_mode_title')}
          </label>
          <DisplayModePicker displayMode={displayMode} setDisplayMode={setDisplayMode} />
        </div>

        <div className="pp-field">
          <label className="pp-field-label">
            <i className="pi pi-eye" />
            {t('happy_hour.preview')}
          </label>
          <BannerPreview
            styleId={style}
            promoText={promoText || t('happy_hour.promo_ph')}
            promoTextEn={promoTextEn}
            imageUrl={imageUrl}
            countdownLabel="30:00"
            progress={1}
          />
        </div>

        <div className="pp-dialog-footer">
          <Button label={t('buttons.cancel')} severity="secondary" outlined onClick={onClose} />
          <Button
            icon="pi pi-check"
            label={t('happy_hour.save')}
            loading={updateMut.isLoading}
            disabled={!canSubmit}
            onClick={submit}
          />
        </div>
      </div>
    </Dialog>
  );
}
